import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Badge,
  Input,
  Button,
  Spinner,
  Collapse,
  InputGroup,
  InputGroupText,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { reportsAPI, projectsAPI } from '../../helpers/api';
import { useIsMobile } from '../../helpers/useMediaQuery';
import SideModal from '../../components/Common/SideModal';
import PayrollDetailModal from './PayrollDetailModal';

interface PayrollSite {
  siteId: string;
  siteName: string;
  projectName: string;
  daysWorked: number;
  totalWageMultiplier: number;
}

interface DailyBreakdownItem {
  date: string;
  siteId: string;
  siteName: string;
  dailyWage: number;
  overtimeHours: number | null;
}

interface LeaveRecord {
  date: string;
  type: string;
}

interface PayrollPersonnel {
  id: string;
  firstName: string;
  lastName: string;
  title: string | null;
  employmentType: 'DAILY' | 'MONTHLY';
  baseDailyWage: number | null;
  monthlySalary: number | null;
  daysWorked: number;
  totalWageMultiplier: number;
  totalOvertimeHours: number;
  overtimePayment: number;
  totalPayment: number;
  leaveDays: number;
  leaves: LeaveRecord[];
  sites: PayrollSite[];
  dailyBreakdown: DailyBreakdownItem[];
}

interface PayrollSummary {
  totalPersonnel: number;
  totalDailyPersonnel: number;
  totalMonthlyPersonnel: number;
  totalPayment: number;
  workingDaysInRange: number;
  dateRange: { start: string; end: string };
}

interface PayrollData {
  personnel: PayrollPersonnel[];
  summary: PayrollSummary;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(amount);

const PayrollReport: React.FC = () => {
  const isMobile = useIsMobile();
  const [data, setData] = useState<PayrollData | null>(null);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [sites, setSites] = useState<any[]>([]);
  const [allSites, setAllSites] = useState<any[]>([]);

  // Filters
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  const firstDay = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
  const lastDayDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const lastDay = `${lastDayDate.getFullYear()}-${pad(lastDayDate.getMonth() + 1)}-${pad(lastDayDate.getDate())}`;
  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDay);
  const [projectFilter, setProjectFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Detail modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState<PayrollPersonnel | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchAllSites();
  }, []);

  useEffect(() => {
    if (projectFilter) {
      const filtered = allSites.filter((s: any) => s.projectId === projectFilter);
      setSites(filtered);
      if (siteFilter && !filtered.find((s: any) => s.id === siteFilter)) {
        setSiteFilter('');
      }
    } else {
      setSites(allSites);
    }
  }, [projectFilter, allSites]);

  const fetchProjects = async () => {
    try {
      const res = await projectsAPI.getAll();
      setProjects(res.data);
    } catch (error) {
      console.error('Projects fetch error:', error);
    }
  };

  const fetchAllSites = async () => {
    try {
      const res = await projectsAPI.getSites();
      setAllSites(res.data);
      setSites(res.data);
    } catch (error) {
      console.error('Sites fetch error:', error);
    }
  };

  const fetchReport = useCallback(async () => {
    if (!startDate || !endDate) return;
    try {
      setLoading(true);
      const res = await reportsAPI.getPersonnelHakedis({
        startDate,
        endDate,
        projectId: projectFilter || undefined,
        siteId: siteFilter || undefined,
      });
      setData(res.data);
    } catch (error: any) {
      console.error('Hakediş fetch error:', error);
      toast.error('Rapor yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, projectFilter, siteFilter]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const clearFilters = () => {
    setProjectFilter('');
    setSiteFilter('');
    setSearchFilter('');
    setStartDate(firstDay);
    setEndDate(lastDay);
  };

  const hasActiveFilters = projectFilter || siteFilter || searchFilter || startDate !== firstDay || endDate !== lastDay;

  const filteredPersonnel = data?.personnel.filter((p) => {
    if (!searchFilter) return true;
    const s = searchFilter.toLowerCase();
    return (
      `${p.firstName} ${p.lastName}`.toLowerCase().includes(s) ||
      (p.title && p.title.toLowerCase().includes(s))
    );
  }) || [];

  const openDetail = (p: PayrollPersonnel) => {
    setSelectedPersonnel(p);
    setDetailModalOpen(true);
  };

  const closeDetail = () => {
    setDetailModalOpen(false);
    setSelectedPersonnel(null);
    setIsExpanded(false);
  };

  const handleExportExcel = async () => {
    if (!data || data.personnel.length === 0) return;
    try {
      const XLSX = await import('xlsx');

      // Sheet 1: Personnel Summary
      const summaryRows = data.personnel.map((p) => ({
        'Ad Soyad': `${p.firstName} ${p.lastName}`,
        'Unvan': p.title || '-',
        'Tip': p.employmentType === 'MONTHLY' ? 'Aylıkçı' : 'Yevmiyeci',
        'Günlük Yevmiye': p.baseDailyWage ?? '-',
        'Aylık Maaş': p.monthlySalary ?? '-',
        'Çalışılan Gün': p.daysWorked,
        'Toplam Yevmiye Çarpanı': p.totalWageMultiplier,
        'Ödenecek Tutar': p.totalPayment,
      }));

      // Add summary row
      summaryRows.push({
        'Ad Soyad': 'TOPLAM',
        'Unvan': '',
        'Tip': `${data.summary.totalDailyPersonnel} Yevmiyeci, ${data.summary.totalMonthlyPersonnel} Aylıkçı`,
        'Günlük Yevmiye': '-',
        'Aylık Maaş': '-',
        'Çalışılan Gün': '-' as any,
        'Toplam Yevmiye Çarpanı': '-' as any,
        'Ödenecek Tutar': data.summary.totalPayment,
      });

      // Sheet 2: Daily Breakdown
      const dailyRows: any[] = [];
      for (const p of data.personnel) {
        for (const d of p.dailyBreakdown) {
          dailyRows.push({
            'Tarih': d.date,
            'Ad Soyad': `${p.firstName} ${p.lastName}`,
            'Unvan': p.title || '-',
            'Şantiye': d.siteName,
            'Yevmiye Çarpanı': d.dailyWage,
            'Mesai (Saat)': d.overtimeHours ?? '-',
          });
        }
      }

      const wb = XLSX.utils.book_new();
      const ws1 = XLSX.utils.json_to_sheet(summaryRows);
      const ws2 = XLSX.utils.json_to_sheet(dailyRows);

      // Column widths
      ws1['!cols'] = [
        { wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 15 },
        { wch: 15 }, { wch: 20 }, { wch: 18 },
      ];
      ws2['!cols'] = [
        { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 25 }, { wch: 18 }, { wch: 14 },
      ];

      XLSX.utils.book_append_sheet(wb, ws1, 'Personel Özet');
      XLSX.utils.book_append_sheet(wb, ws2, 'Günlük Döküm');

      const fileName = `Hakedis_${data.summary.dateRange.start}_${data.summary.dateRange.end}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success('Excel dosyası indirildi');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Excel dışa aktarma hatası');
    }
  };

  const detailHeaderActions = (
    <button
      type="button"
      className="side-modal-expand-btn"
      onClick={() => setIsExpanded(!isExpanded)}
      title={isExpanded ? 'Küçült' : 'Genişlet'}
    >
      <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
    </button>
  );

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">Hakediş Raporu</h4>
            <div className="page-title-right">
              {data && data.personnel.length > 0 && (
                <Button color="success" size="sm" onClick={handleExportExcel}>
                  <i className="ri-file-excel-2-line me-1"></i>
                  Excel İndir
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Summary Cards */}
      {data && (
        <Row className="mb-3">
          <Col sm={6} lg={3} className="mb-2">
            <Card className="card-animate mb-0">
              <CardBody className="p-3">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-primary-subtle text-primary rounded-circle fs-4">
                      <i className="ri-team-line"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-1 fs-12">Toplam Personel</p>
                    <h5 className="mb-0">{data.summary.totalPersonnel}</h5>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col sm={6} lg={3} className="mb-2">
            <Card className="card-animate mb-0">
              <CardBody className="p-3">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-info-subtle text-info rounded-circle fs-4">
                      <i className="ri-user-line"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-1 fs-12">Yevmiyeci</p>
                    <h5 className="mb-0">{data.summary.totalDailyPersonnel}</h5>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col sm={6} lg={3} className="mb-2">
            <Card className="card-animate mb-0">
              <CardBody className="p-3">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-warning-subtle text-warning rounded-circle fs-4">
                      <i className="ri-user-star-line"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-1 fs-12">Aylıkçı</p>
                    <h5 className="mb-0">{data.summary.totalMonthlyPersonnel}</h5>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col sm={6} lg={3} className="mb-2">
            <Card className="card-animate mb-0">
              <CardBody className="p-3">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-success-subtle text-success rounded-circle fs-4">
                      <i className="ri-money-dollar-circle-line"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-1 fs-12">Toplam Ödeme</p>
                    <h5 className="mb-0">{formatCurrency(data.summary.totalPayment)}</h5>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={3}>
                  <h5 className="card-title mb-0">
                    Personel Listesi
                    {data && <span className="text-muted ms-1 fs-12">({data.summary.workingDaysInRange} iş günü)</span>}
                  </h5>
                </Col>
                {!isMobile && (
                  <Col md={9}>
                    <Row className="g-2 align-items-center justify-content-end">
                      <Col md="auto">
                        <Input
                          type="text"
                          bsSize="sm"
                          value={searchFilter}
                          onChange={(e) => setSearchFilter(e.target.value)}
                          placeholder="Ad soyad ara..."
                          style={{ width: '150px' }}
                        />
                      </Col>
                      <Col md="auto">
                        <Input
                          type="select"
                          bsSize="sm"
                          value={projectFilter}
                          onChange={(e) => setProjectFilter(e.target.value)}
                          style={{ minWidth: '160px' }}
                        >
                          <option value="">Tüm Projeler</option>
                          {projects.map((p: any) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </Input>
                      </Col>
                      <Col md="auto">
                        <Input
                          type="select"
                          bsSize="sm"
                          value={siteFilter}
                          onChange={(e) => setSiteFilter(e.target.value)}
                          style={{ minWidth: '160px' }}
                        >
                          <option value="">Tüm Şantiyeler</option>
                          {sites.map((s: any) => (
                            <option key={s.id} value={s.id}>
                              {s.project?.name ? `${s.project.name} / ` : ''}{s.name}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md="auto">
                        <InputGroup size="sm">
                          <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            style={{ maxWidth: '140px' }}
                          />
                          <InputGroupText>-</InputGroupText>
                          <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            style={{ maxWidth: '140px' }}
                          />
                        </InputGroup>
                      </Col>
                      {hasActiveFilters && (
                        <Col md="auto">
                          <Button color="light" size="sm" onClick={clearFilters} title="Filtreleri Temizle">
                            <i className="ri-close-line"></i>
                          </Button>
                        </Col>
                      )}
                    </Row>
                  </Col>
                )}
              </Row>
            </CardHeader>
            <CardBody>
              {/* Mobile Filters */}
              {isMobile && (
                <>
                  <button
                    className="btn btn-sm btn-light w-100 mb-3 d-flex align-items-center justify-content-between"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <span>
                      <i className="ri-filter-3-line me-2"></i>
                      Filtreler
                      {hasActiveFilters && (
                        <Badge color="primary" className="ms-2">
                          {[projectFilter, siteFilter].filter(Boolean).length + (startDate !== firstDay || endDate !== lastDay ? 1 : 0)}
                        </Badge>
                      )}
                    </span>
                    <i className={`ri-arrow-${showFilters ? 'up' : 'down'}-s-line`}></i>
                  </button>

                  <Collapse isOpen={showFilters}>
                    <div className="bg-light p-3 rounded mb-3">
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Ara</label>
                        <Input
                          type="text"
                          bsSize="sm"
                          value={searchFilter}
                          onChange={(e) => setSearchFilter(e.target.value)}
                          placeholder="Ad soyad ara..."
                        />
                      </div>
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Proje</label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={projectFilter}
                          onChange={(e) => setProjectFilter(e.target.value)}
                        >
                          <option value="">Tüm Projeler</option>
                          {projects.map((p: any) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </Input>
                      </div>
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Şantiye</label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={siteFilter}
                          onChange={(e) => setSiteFilter(e.target.value)}
                        >
                          <option value="">Tüm Şantiyeler</option>
                          {sites.map((s: any) => (
                            <option key={s.id} value={s.id}>
                              {s.project?.name ? `${s.project.name} / ` : ''}{s.name}
                            </option>
                          ))}
                        </Input>
                      </div>
                      <Row>
                        <Col xs={6}>
                          <div className="mb-2">
                            <label className="small text-muted mb-1">Başlangıç</label>
                            <Input
                              type="date"
                              bsSize="sm"
                              value={startDate}
                              onChange={(e) => setStartDate(e.target.value)}
                            />
                          </div>
                        </Col>
                        <Col xs={6}>
                          <div className="mb-2">
                            <label className="small text-muted mb-1">Bitiş</label>
                            <Input
                              type="date"
                              bsSize="sm"
                              value={endDate}
                              onChange={(e) => setEndDate(e.target.value)}
                            />
                          </div>
                        </Col>
                      </Row>
                      {hasActiveFilters && (
                        <button
                          className="btn btn-sm btn-outline-secondary w-100 mt-2"
                          onClick={clearFilters}
                        >
                          <i className="ri-close-line me-1"></i>
                          Filtreleri Temizle
                        </button>
                      )}
                    </div>
                  </Collapse>
                </>
              )}

              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                  </div>
                </div>
              ) : !data || filteredPersonnel.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">Seçilen tarih aralığında kayıt bulunmuyor.</p>
                </div>
              ) : isMobile ? (
                // Mobile Card View
                <div className="admin-card-list">
                  {filteredPersonnel.map((p) => (
                    <div key={p.id} className="admin-card-item">
                      <div
                        className="admin-card-header"
                        onClick={() => openDetail(p)}
                        style={{ cursor: 'pointer' }}
                      >
                        <h6 className="admin-card-title">
                          {p.firstName} {p.lastName}
                        </h6>
                        <Badge color={p.employmentType === 'MONTHLY' ? 'warning' : 'info'}>
                          {p.employmentType === 'MONTHLY' ? 'Aylıkçı' : 'Yevmiyeci'}
                        </Badge>
                      </div>
                      <div
                        className="admin-card-body"
                        onClick={() => openDetail(p)}
                        style={{ cursor: 'pointer' }}
                      >
                        {p.title && (
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-briefcase-line"></i>
                              Unvan
                            </span>
                            <span className="admin-info-value">{p.title}</span>
                          </div>
                        )}
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-calendar-check-line"></i>
                            Çalışılan Gün
                          </span>
                          <span className="admin-info-value">{p.daysWorked} gün</span>
                        </div>
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-money-dollar-circle-line"></i>
                            Ödenecek
                          </span>
                          <span className="admin-info-value fw-semibold text-success">
                            {formatCurrency(p.totalPayment)}
                          </span>
                        </div>
                      </div>
                      <div className="admin-card-footer">
                        <button
                          type="button"
                          className="admin-action-btn admin-btn-primary"
                          style={{ flex: 1 }}
                          onClick={() => openDetail(p)}
                        >
                          <i className="ri-eye-line me-1"></i>
                          Detay
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Desktop Table View
                <div className="table-responsive">
                  <Table className="table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Ad Soyad</th>
                        <th>Unvan</th>
                        <th>Tip</th>
                        <th className="text-center">Çalışılan Gün</th>
                        <th className="text-center">İzin</th>
                        <th className="text-center">Yevmiye Çarpanı</th>
                        <th className="text-center">Mesai</th>
                        <th className="text-end">Ödenecek Tutar</th>
                        <th className="text-center" style={{ width: '80px' }}>Detay</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPersonnel.map((p) => (
                        <tr
                          key={p.id}
                          onClick={() => openDetail(p)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <strong>{p.firstName} {p.lastName}</strong>
                          </td>
                          <td>{p.title || '-'}</td>
                          <td>
                            <Badge color={p.employmentType === 'MONTHLY' ? 'warning' : 'info'}>
                              {p.employmentType === 'MONTHLY' ? 'Aylıkçı' : 'Yevmiyeci'}
                            </Badge>
                          </td>
                          <td className="text-center">{p.daysWorked}</td>
                          <td className="text-center">
                            {p.leaveDays > 0 ? (
                              <Badge color="danger">{p.leaveDays}</Badge>
                            ) : '-'}
                          </td>
                          <td className="text-center">{p.totalWageMultiplier}</td>
                          <td className="text-center">
                            {p.totalOvertimeHours > 0 ? (
                              <Badge color="warning">{p.totalOvertimeHours} sa</Badge>
                            ) : '-'}
                          </td>
                          <td className="text-end fw-semibold">{formatCurrency(p.totalPayment)}</td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-light"
                              onClick={(e) => { e.stopPropagation(); openDetail(p); }}
                            >
                              <i className="ri-eye-line"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="table-light fw-bold">
                        <td colSpan={3}>TOPLAM ({data.summary.totalPersonnel} kişi)</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-center">-</td>
                        <td className="text-end text-success">{formatCurrency(data.summary.totalPayment)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Detail Modal */}
      <SideModal
        isOpen={detailModalOpen}
        toggle={closeDetail}
        title={selectedPersonnel ? `${selectedPersonnel.firstName} ${selectedPersonnel.lastName}` : 'Detay'}
        size={isExpanded ? 'expanded' : 'lg'}
        headerActions={detailHeaderActions}
      >
        {selectedPersonnel && <PayrollDetailModal personnel={selectedPersonnel} />}
      </SideModal>
    </>
  );
};

export default PayrollReport;
